import { useCallback, useMemo, useState } from 'react';
import type {
  AtomSymbolConstraint,
  CycleSizeConstraint,
  NumericComparator,
  StructuralConstraints
} from '@peruselab/api-client/moleculeSearch';

// #566: the Filter tab's Cycle Sizes / Atom Symbols sections -- an ordered list of independently
// addable/removable rows per section (the reference design's repeatable-row UI), server-side
// filters (#564/#565) like reactionBuckets, not client-only post-filters like categories/types.
let nextRowId = 0;
function newRowId(prefix: string): string {
  nextRowId += 1;
  return `${prefix}-${nextRowId}`;
}

export interface CycleSizeRow extends CycleSizeConstraint {
  readonly id: string;
}

export interface AtomSymbolRow extends AtomSymbolConstraint {
  readonly id: string;
}

const DEFAULT_CYCLE_SIZE: Omit<CycleSizeRow, 'id'> = { ringSize: 6, comparator: 'EQ', count: 1 };
const DEFAULT_ATOM_SYMBOL: Omit<AtomSymbolRow, 'id'> = { symbol: 'C', comparator: 'GE', count: 1 };

function stripId<T extends { readonly id: string }>(row: T): Omit<T, 'id'> {
  const { id: _id, ...rest } = row;
  return rest;
}

export function useStructuralConstraints() {
  const [cycleSizeRows, setCycleSizeRows] = useState<readonly CycleSizeRow[]>([]);
  const [atomSymbolRows, setAtomSymbolRows] = useState<readonly AtomSymbolRow[]>([]);

  const addCycleSizeRow = useCallback(() => {
    setCycleSizeRows((rows) => [...rows, { id: newRowId('cycle-size'), ...DEFAULT_CYCLE_SIZE }]);
  }, []);
  const removeCycleSizeRow = useCallback((id: string) => {
    setCycleSizeRows((rows) => rows.filter((row) => row.id !== id));
  }, []);
  const updateCycleSizeRow = useCallback((id: string, patch: Partial<CycleSizeConstraint>) => {
    setCycleSizeRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  const addAtomSymbolRow = useCallback(() => {
    setAtomSymbolRows((rows) => [...rows, { id: newRowId('atom-symbol'), ...DEFAULT_ATOM_SYMBOL }]);
  }, []);
  const removeAtomSymbolRow = useCallback((id: string) => {
    setAtomSymbolRows((rows) => rows.filter((row) => row.id !== id));
  }, []);
  const updateAtomSymbolRow = useCallback((id: string, patch: Partial<AtomSymbolConstraint>) => {
    setAtomSymbolRows((rows) => rows.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  }, []);

  // The actual server-bound value -- the client-only `id` never leaves this hook.
  const structuralConstraints = useMemo<StructuralConstraints>(
    () => ({ cycleSizes: cycleSizeRows.map(stripId), atomSymbols: atomSymbolRows.map(stripId) }),
    [cycleSizeRows, atomSymbolRows]
  );

  return {
    cycleSizeRows,
    addCycleSizeRow,
    removeCycleSizeRow,
    updateCycleSizeRow,
    atomSymbolRows,
    addAtomSymbolRow,
    removeAtomSymbolRow,
    updateAtomSymbolRow,
    structuralConstraints
  };
}

export type UseStructuralConstraintsResult = ReturnType<typeof useStructuralConstraints>;
export type { NumericComparator };
